import React, { useMemo, useState, useEffect } from "react";
import { Badge, Avatar, Circlebar, Button, Card } from "components/ui";
import { toast } from "sonner";
import {
  PhoneIcon,
  ClockIcon,
  EyeIcon,
  EnvelopeIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  CheckBadgeIcon,
  UserIcon,
  DocumentDuplicateIcon,
  ChevronDownIcon,
  XMarkIcon,
  XCircleIcon,
  PencilSquareIcon,
  ArrowPathIcon,
  FlagIcon,
  FireIcon,
  PaperAirplaneIcon,
  PlusIcon,
  
} from "@heroicons/react/24/outline";
import {
  HeartIcon as HeartSolid,
  FlagIcon as FlagSolid,
} from "@heroicons/react/24/solid";
import {
  calculatePriority,
  getScoreLabel,
} from "../../../utils/priorityCalculator";
// import LeadTimelineDrawer from "./LeadTimelineDrawer";
import { Tooltip } from "../../shared/Tooltip";
import { hasPermission } from "../../../utils/permissionsUtils";
import { createLeadUser, getLeadDuplicates } from "../../../services/leadsService";
import { useLocation, Link } from "react-router";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { DuplicateLeadCard } from "../shared/DuplicateLeadCard";
import { counselorVerifyPhoneEmail } from "../../../services/leadsService";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
  Dialog,
} from "@headlessui/react";
import { Fragment } from "react";
import clsx from "clsx";
import { formatDisplayDate } from "../../../utils/dateUtils";
import LeadTimeline from "./TimeLineView";
import EmailMessageModal from "../LeadActions/EmailMessageModal";

const LeadBasicInfo = ({
  leadData,
  isFavorite,
  isFlaged,
  selectedStage,
  selectedSubStage,
  isMobile,
  onEditStage,
  onEditPriority,
  onEditInteractionLevel,
  onRefreshLead,
  // Lead Management handlers
  onChangeStage,
  onSetPriority,
  onSetInteractionLevel,
  onOpenNotesModal,
  onEmailClick,
  entityName ,
}) => {
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [isLoadingDuplicates, setIsLoadingDuplicates] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [isVerifying, setIsVerifying] = useState({ phone: false, email: false });
  const [showEmailModal, setShowEmailModal] = useState(false);

  const location = useLocation();
  const isManager = location.pathname.includes("manager");
  const duplicateAnchorId = useMemo(
    () => `lead-basic-duplicate-anchor-${leadData?.LeadId || leadData?.id}`,
    [leadData?.LeadId, leadData?.id],
  );
  const duplicateTooltipId = `${duplicateAnchorId}-tooltip`;

  const isPhoneVerified =
    leadData?.PhoneVerified === true

  const isEmailVerified =
    leadData?.EmailVerified === true

  const [showTimelineDrawer, setShowTimelineDrawer] = useState(false);
  const leadId = leadData?.LeadId || leadData?.id;

  // Auto-load duplicates when lead has duplicates
  React.useEffect(() => {
    if (leadData?.HasDuplicate && duplicates.length === 0 && !isLoadingDuplicates) {
      loadDuplicates();
    }
  }, [leadData?.id, leadData?.HasDuplicate]);

  const handleToggleDuplicates = (event) => {
    event?.preventDefault?.();
    if (!leadData?.HasDuplicate) return;

    if (showDuplicates) {
      setShowDuplicates(false);
      return;
    }

    if (duplicates.length === 0 && !isLoadingDuplicates) {
      loadDuplicates();
    }

    setShowDuplicates(true);
  };

  React.useEffect(() => {
    if (!leadData?.HasDuplicate) {
      setShowDuplicates(false);
    }
  }, [leadData?.HasDuplicate]);

  const handleCreateUser = async () => {
    setIsCreatingUser(true);
    try {
      const result = await createLeadUser(leadData?.id);
      if (result.success) {
        toast.success(result.message || "User created successfully");
        if (onRefreshLead) onRefreshLead();
      } else {
        toast.error(result.message || "Failed to create user");
      }
    } catch (error) {
      console.error("Error creating user:", error);
      toast.error("Failed to create user");
    } finally {
      setIsCreatingUser(false);
    }
  };
  const loadDuplicates = async () => {
    setIsLoadingDuplicates(true);
    try {
      const result = await getLeadDuplicates(leadData?.id);
      if (result.success) {
        setDuplicates(result.data || []);
      } else {
        console.error("Failed to fetch duplicates:", result.message);
      }
    } catch (error) {
      console.error("Error fetching duplicates:", error);
    } finally {
      setIsLoadingDuplicates(false);
    }
  };

  // Respect the prop if provided, otherwise determine from pathname
  const resolvedEntityName = entityName || (isManager ? "Lead" : "Applicant");

  const handleVerifyPhoneEmail = async (isPhone, close) => {
    const fieldType = isPhone ? "phone" : "email";
    const fieldValue = isPhone ? leadData?.mobile : leadData?.email;

    if (!fieldValue) {
      toast.error(`No ${fieldType} found to verify`);
      close();
      return;
    }

    setIsVerifying({ ...isVerifying, [fieldType]: true });

    try {
      const payload = {
        leadId: leadData?.id,
        fieldValue: fieldValue,
        isPhone: isPhone,
        isEmail: !isPhone
      };

      const result = await counselorVerifyPhoneEmail(payload);

      if (result.success) {
        toast.success(result.message || `${isPhone ? 'Phone' : 'Email'} verified successfully`);
        /** REFETCH LEAD DATA */
        if (onRefreshLead) onRefreshLead();
      } else {
        toast.error(result.message || `Failed to verify ${fieldType}`);
      }
    } catch (error) {
      console.error(`Error verifying ${fieldType}:`, error);
      toast.error(`Failed to verify ${fieldType}`);
    } finally {
      setIsVerifying({ ...isVerifying, [fieldType]: false });
      close();
    }
  };


  const getLeadScoreColor = (score) => {
    const priority = calculatePriority(score);
    switch (priority.id) {
      case "hot":
      case "urgent":
        return "error";
      case "high":
        return "warning";
      case "medium":
        return "primary";
      case "low":
        return "secondary";
      default:
        return "neutral";
    }
  };

  // Add null check for leadData
  if (!leadData) {
    return null;
  }

  return (
    <>
      <div
        className={`mb-4 flex flex-col space-y-4 px-2 sm:mb-6 sm:px-4 lg:flex-row lg:items-start lg:justify-between lg:space-y-0 lg:px-0`}
      >
        <div className={`flex items-start gap-3 sm:gap-4`}>
          <Avatar
            name={leadData?.name || "Unknown"}
            size={16}
            initialColor="primary"
            className="shrink-0"
          />
          <div className="min-w-0 flex-1">
            <div className={`mb-1 flex flex-wrap items-center gap-2 sm:gap-3`}>
              <h1
                className={`dark:text-dark-50 flex items-center gap-2 truncate text-lg font-semibold text-gray-900 sm:text-xl`}
              >
                <button
                  type="button"
                  onClick={() => !leadData?.IsStudent && onEmailClick?.()}
                  className={clsx(
                    "transition-colors text-left",
                    !leadData?.IsStudent ? "hover:underline hover:text-primary-600 dark:hover:text-primary-400 cursor-pointer" : "cursor-default"
                  )}
                  title={leadData?.IsStudent ? "" : "Send Email"}
                >
                  {leadData?.name} {leadData?.FatherName ? ` ${leadData?.gender?.trim()?.toLowerCase() === 'male' ? 'S/O' : leadData?.gender?.trim()?.toLowerCase() === 'female' ? 'D/O' : 'C/O'} ${leadData.FatherName}` : ""}
                </button>
                {leadData?.HasDuplicate && <Badge color="error" className="font-semibold p-1 px-2 text-[10px]" variant="filled">P</Badge>}
              </h1>
              <div className="group relative inline-flex items-center gap-1">
                <Badge
                  variant="soft"
                  style={{ color: leadData?.InteractionLevelColorCode }}
                  className="text-xs font-medium"
                >
                  {leadData?.InteractionLevelName}
                </Badge>
                {onEditInteractionLevel && !leadData?.IsStudent && (
                  <button
                    onClick={onEditInteractionLevel}
                    className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                    title="Edit Interaction Level"
                  >
                    <PencilSquareIcon className="h-3 w-3 text-gray-500 dark:text-gray-400" />
                  </button>
                )}
              </div>
              {leadData?.InteractionLevelDescription && (
                <>
                  <InformationCircleIcon
                    className="size-4 cursor-help text-gray-400 transition-colors hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                    data-tooltip-id={`priority-tooltip-${leadData?.id || Math.random()}`}
                    data-tooltip-content={leadData?.InteractionLevelDescription}
                  />
                  <Tooltip
                    id={`priority-tooltip-${leadData?.id || Math.random()}`}
                    place="top"
                    effect="solid"
                  />
                </>
              )}
              {isFavorite && (
                <HeartSolid
                  className={`h-4 w-4 shrink-0 text-red-500 sm:h-5 sm:w-5`}
                />
              )}
              {isFlaged && (
                <FlagSolid
                  className={`text-warning-500 h-4 w-4 shrink-0 sm:h-5 sm:w-5`}
                />
              )}
              {leadData?.HasDuplicate && (
                <button
                  type="button"
                  id={duplicateAnchorId}
                  onClick={handleToggleDuplicates}
                  disabled={isLoadingDuplicates && !showDuplicates && duplicates.length === 0}
                  className={clsx(
                    "inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700 transition hover:bg-amber-200",
                    "disabled:cursor-not-allowed disabled:opacity-60 dark:bg-amber-500/20 dark:text-amber-200",
                  )}
                  aria-expanded={showDuplicates}
                >
                  <DocumentDuplicateIcon className="size-3.5" />
                  <span>
                    {isLoadingDuplicates && duplicates.length === 0
                      ? "Loading"
                      : duplicates.length > 0
                        ? `${duplicates.length} Duplicate${duplicates.length > 1 ? "s" : ""}`
                        : "View Duplicates"}
                  </span>
                  <ChevronDownIcon
                    className={clsx(
                      "size-3 transition-transform",
                      showDuplicates && "rotate-180",
                    )}
                  />
                </button>
              )}
              {/* Create User Button */}
              {!leadData?.LeadUserId && !leadData?.IsDuplicate && hasPermission("MLLE") && !leadData?.IsStudent && (
                <Button
                  onClick={handleCreateUser}
                  disabled={isCreatingUser}
                  variant="soft"
                  color="primary"
                  size="sm"
                  className="inline-flex items-center gap-2 ms-2 mb-2 sm:mb-0"
                >
                  <UserIcon className="h-4 w-4" />
                  <span>{isCreatingUser ? "Creating..." : "Create User"}</span>
                </Button>
              )}
              {/* Add Note Button */}
              <Button
                onClick={onOpenNotesModal}
                disabled={leadData?.IsStudent}
                variant="soft"
                color="success"
                size="sm"
                className={`inline-flex items-center gap-2 ms-2 mb-2 sm:mb-0 ${leadData?.IsStudent ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <PlusIcon className="h-4 w-4" />
                <span>Add Note</span>
              </Button>
            </div>
            <div className={`mb-2 flex flex-wrap items-center gap-1`}>
              {/* Lead ID */}
              <div className="flex min-w-[100px] items-start gap-1">
                <span className="dark:text-dark-300 text-xs text-gray-500 sm:text-sm">
                  Lead ID:
                </span>
                <span className="dark:text-dark-300 truncate text-xs text-gray-500 sm:text-sm">
                  {leadData.displayId || leadData.id}
                </span>
              </div>
              {/* Priority with hover edit */}
              <div className="flex min-w-[100px] items-start gap-1 group relative">
                <span className="dark:text-dark-300 text-xs text-gray-500 sm:text-sm">
                  Priority:
                </span>
                <span className="dark:text-dark-300 truncate text-xs text-gray-500 sm:text-sm">
                  {leadData?.priority || "N/A"}
                </span>
                {onEditPriority && !leadData?.IsStudent && (
                  <button
                    onClick={onEditPriority}
                    className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                    title="Edit Priority"
                  >
                    <PencilSquareIcon className="h-3 w-3 text-gray-500 dark:text-gray-400" />
                  </button>
                )}
              </div>
              {/* Source */}
              <div className="flex min-w-[120px] items-start gap-1">
                <span className="dark:text-dark-300 text-xs text-gray-500 sm:text-sm">
                  Source:
                </span>
                <span className="dark:text-dark-300 truncate text-xs text-gray-500 sm:text-sm">
                  {leadData.source || "N/A"}
                </span>
              </div>
            </div>
            <div className={`mb-2 flex flex-wrap items-center gap-1`}>
              {/* Stage with hover edit */}
              <div className="flex min-w-[110px] items-start gap-1 group relative">
                <span className="dark:text-dark-400 text-xs text-gray-500 sm:text-sm">
                  Stage:
                </span>
                <span className="dark:text-dark-300 truncate text-xs text-gray-500 sm:text-sm">
                  {selectedStage}
                </span>
                {onEditStage && !leadData?.IsStudent && (
                  <button
                    onClick={onEditStage}
                    className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                    title="Edit Stage"
                  >
                    <PencilSquareIcon className="h-3 w-3 text-gray-500 dark:text-gray-400" />
                  </button>
                )}
              </div>

              {/* Substage */}
              {selectedSubStage !== "N/A" && (
                <div className="flex min-w-[140px] items-start gap-1">
                  <span className="dark:text-dark-400 text-xs text-gray-500 sm:text-sm">
                    Substage:
                  </span>
                  <span className="dark:text-dark-300 text-xs break-words text-gray-500 sm:text-sm">
                    {selectedSubStage}
                  </span>
                </div>
              )}
            </div>

            {/* Phone and Email on first line */}
            <div
              className="dark:text-dark-300 flex flex-col gap-2 text-xs text-gray-600 
             sm:flex-row sm:items-start sm:gap-4 sm:text-sm"
            >
              {/* Phone */}
              <div className="flex min-w-0 items-center gap-1">
                <PhoneIcon className="h-3 w-3 shrink-0 sm:h-4 sm:w-4" />
                <span className="truncate">
                  {leadData.mobile}{" "}
                  {leadData.mobile && leadData.mobile !== "N/A" && (
                    <Popover className="relative inline-block">
                      <PopoverButton
                        disabled={leadData?.IsStudent}
                        className={clsx(
                          "ml-1 inline-block rounded-full px-2 py-0.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1",
                          isPhoneVerified
                            ? "bg-green-100 text-green-700 hover:bg-green-200 focus:ring-green-500"
                            : "bg-red-100 text-red-700 hover:bg-red-200 focus:ring-red-500",
                          leadData?.IsStudent && "opacity-60 cursor-not-allowed"
                        )}
                      >
                        {isPhoneVerified ? 'Verified' : 'Not Verified'}
                      </PopoverButton>
                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-200"
                        enterFrom="opacity-0 translate-y-2"
                        enterTo="opacity-100 translate-y-0"
                        leave="transition ease-in duration-150"
                        leaveFrom="opacity-100 translate-y-0"
                        leaveTo="opacity-0 translate-y-2"
                      >
                        <PopoverPanel
                          anchor={{ to: "bottom start", gap: 8 }}
                          className="z-[100] w-80 rounded-md border border-gray-300 bg-white p-4 shadow-lg shadow-gray-200/50 outline-none ring-primary-500/50 focus-visible:outline-none focus-visible:ring dark:border-dark-500 dark:bg-dark-750 dark:shadow-none"
                        >
                          {({ close }) => (
                            <>
                              <div className="flex items-start gap-3">
                                <div className="flex-shrink-0">
                                  <ExclamationTriangleIcon className="h-6 w-6 text-amber-500" />
                                </div>
                                <div className="flex-1">
                                  <h3 className="text-base font-semibold tracking-wide text-gray-800 dark:text-dark-100">
                                    {isPhoneVerified ? 'Unverify Phone Number' : 'Verify Phone Number'}
                                  </h3>
                                  <p className="mt-2 text-sm text-gray-600 dark:text-dark-300">
                                    Are you sure you want to {isPhoneVerified ? 'unverify' : 'verify'} this phone number?
                                  </p>
                                  <p className="mt-1 text-sm font-medium text-gray-700 dark:text-dark-200">
                                    {leadData.mobile}
                                  </p>
                                </div>
                              </div>
                              <div className="mt-4 flex gap-2 justify-end">
                                <Button
                                  onClick={() => close()}
                                  variant="outlined"
                                  color="neutral"
                                  size="sm"
                                  disabled={isVerifying.phone}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  onClick={() => handleVerifyPhoneEmail(true, close)}
                                  variant="filled"
                                  color={isPhoneVerified ? "error" : "primary"}
                                  size="sm"
                                  disabled={isVerifying.phone}
                                  className="inline-flex items-center gap-2"
                                >
                                  {isVerifying.phone ? (
                                    <>
                                      <ArrowPathIcon className="h-4 w-4 animate-spin" />
                                      {isPhoneVerified ? 'Unverifying...' : 'Verifying...'}
                                    </>
                                  ) : (
                                    <>
                                      <CheckBadgeIcon className="h-4 w-4" />
                                      {isPhoneVerified ? 'Unverify' : 'Verify'}
                                    </>
                                  )}
                                </Button>
                              </div>
                            </>
                          )}
                        </PopoverPanel>
                      </Transition>
                    </Popover>
                  )}
                </span>
              </div>

              {/* Email */}
              <div className="flex min-w-0 items-center gap-1 group">
                <EnvelopeIcon className="h-3 w-3 shrink-0 sm:h-4 sm:w-4 group-hover:text-primary-600 transition-colors" />
                <div className="flex items-center gap-1 min-w-0">
                  <span
                    className={clsx(
                      "truncate transition-colors font-medium",
                      !leadData?.IsStudent ? "cursor-pointer hover:text-primary-600 hover:underline" : "cursor-default"
                    )}
                    onClick={() => !leadData?.IsStudent && setShowEmailModal(true)}
                    title={leadData?.IsStudent ? "" : "Send Email"}
                  >
                    {leadData.email}
                  </span>
                  {leadData.email && leadData.email !== "N/A" && (
                    <Popover className="relative inline-block">
                      <PopoverButton
                        disabled={leadData?.IsStudent}
                        className={clsx(
                          "ml-1 inline-block rounded-full px-2 py-0.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1",
                          isEmailVerified
                            ? "bg-green-100 text-green-700 hover:bg-green-200 focus:ring-green-500"
                            : "bg-red-100 text-red-700 hover:bg-red-200 focus:ring-red-500",
                          leadData?.IsStudent && "opacity-60 cursor-not-allowed"
                        )}
                      >
                        {isEmailVerified ? 'Verified' : 'Not Verified'}
                      </PopoverButton>
                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-200"
                        enterFrom="opacity-0 translate-y-2"
                        enterTo="opacity-100 translate-y-0"
                        leave="transition ease-in duration-150"
                        leaveFrom="opacity-100 translate-y-0"
                        leaveTo="opacity-0 translate-y-2"
                      >
                        <PopoverPanel
                          anchor={{ to: "bottom start", gap: 8 }}
                          className="z-[100] w-80 rounded-md border border-gray-300 bg-white p-4 shadow-lg shadow-gray-200/50 outline-none ring-primary-500/50 focus-visible:outline-none focus-visible:ring dark:border-dark-500 dark:bg-dark-750 dark:shadow-none"
                        >
                          {({ close }) => (
                            <>
                              <div className="flex items-start gap-3">
                                <div className="flex-shrink-0">
                                  <ExclamationTriangleIcon className="h-6 w-6 text-amber-500" />
                                </div>
                                <div className="flex-1">
                                  <h3 className="text-base font-semibold tracking-wide text-gray-800 dark:text-dark-100">
                                    {isEmailVerified ? 'Unverify Email Address' : 'Verify Email Address'}
                                  </h3>
                                  <p className="mt-2 text-sm text-gray-600 dark:text-dark-300">
                                    Are you sure you want to {isEmailVerified ? 'unverify' : 'verify'} this email address?
                                  </p>
                                  <p className="mt-1 text-sm font-medium text-gray-700 dark:text-dark-200 break-all">
                                    {leadData.email}
                                  </p>
                                </div>
                              </div>
                              <div className="mt-4 flex gap-2 justify-end">
                                <Button
                                  onClick={() => close()}
                                  variant="outlined"
                                  color="neutral"
                                  size="sm"
                                  disabled={isVerifying.email}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  onClick={() => handleVerifyPhoneEmail(false, close)}
                                  variant="filled"
                                  color={isEmailVerified ? "error" : "primary"}
                                  size="sm"
                                  disabled={isVerifying.email}
                                  className="inline-flex items-center gap-2"
                                >
                                  {isVerifying.email ? (
                                    <>
                                      <ArrowPathIcon className="h-4 w-4 animate-spin" />
                                      {isEmailVerified ? 'Unverifying...' : 'Verifying...'}
                                    </>
                                  ) : (
                                    <>
                                      <CheckBadgeIcon className="h-4 w-4" />
                                      {isEmailVerified ? 'Unverify' : 'Verify'}
                                    </>
                                  )}
                                </Button>
                              </div>
                            </>
                          )}
                        </PopoverPanel>
                      </Transition>
                    </Popover>
                  )}
                </div>
              </div>
            </div>

            {/* Added and Active on second line */}
            <div
              className={`dark:text-dark-300 flex flex-col gap-2 text-xs text-gray-600 sm:flex-row sm:items-start sm:gap-4 sm:text-sm mt-2`}
            >
              <div className="flex min-w-0 items-center gap-1">
                <ClockIcon className={`h-3 w-3 shrink-0 sm:h-4 sm:w-4`} />
                <span className="truncate">
                  Added:{" "}
                  {leadData.addedOn
                    ? formatDisplayDate(leadData.addedOn)
                    : "N/A"}
                </span>
              </div>
              <div className="flex min-w-0 items-center gap-1">
                <EyeIcon className={`h-3 w-3 shrink-0 sm:h-4 sm:w-4`} />
                <span className="truncate">
                  Last Logged In:{" "}
                  {leadData?.lastActive
                    ? formatDisplayDate(leadData.lastActive)
                    : "N/A"}
                </span>
              </div>
            </div>

            {/* Tags horizontal display */}
            {(() => {
              const rawTags = leadData?.Tags ?? leadData?.tags;
              const tags = typeof rawTags === "string"
                ? (() => { try { return JSON.parse(rawTags); } catch { return []; } })()
                : Array.isArray(rawTags) ? rawTags : [];

              if (tags.length === 0) return null;

              return (
                <div className="mt-2 flex items-center gap-2 text-xs sm:text-sm">
                  <span className="dark:text-dark-400 text-gray-500 font-medium">Tags:</span>
                  <div className="flex items-center">
                    {tags.length === 1 ? (
                      (() => {
                        const tagText = typeof tags[0] === "string" ? tags[0] : tags[0]?.TagDisplayName || tags[0]?.name || "";
                        return (
                          <span
                            title={tagText}
                            className="inline-flex items-center max-w-[120px] truncate rounded-full bg-green-100 px-2.5 py-0.5 text-[10px] font-medium text-green-800 dark:bg-green-900/30 dark:text-green-300"
                          >
                            {tagText}
                          </span>
                        );
                      })()
                    ) : (
                      (() => {
                        const firstTagText = typeof tags[0] === "string" ? tags[0] : tags[0]?.TagDisplayName || tags[0]?.name || "";
                        const otherTags = tags.slice(1);
                        return (
                          <Popover as="div" className="relative inline-flex items-center gap-1.5">
                            {({ open }) => (
                              <>

                                <span
                                  title={firstTagText}
                                  className="inline-flex items-center max-w-[120px] truncate rounded-full bg-green-100 px-2.5 py-0.5 text-[10px] font-medium text-green-800 dark:bg-green-900/30 dark:text-green-300"
                                >
                                  {firstTagText}
                                </span>

                                <PopoverButton
                                  className={clsx(
                                    "inline-flex h-6 min-w-6 px-1.5 items-center justify-center rounded-full bg-gray-100 text-[11px] font-bold text-gray-600 hover:bg-gray-200 focus:outline-none dark:bg-dark-600 dark:text-dark-200 dark:hover:bg-dark-500 cursor-pointer transition-colors",
                                    open && "invisible pointer-events-none"
                                  )}
                                >
                                  +{otherTags.length}
                                </PopoverButton>

                                <Transition
                                  as={Fragment}
                                  enter="transition ease-out duration-100"
                                  enterFrom="opacity-0 scale-95 translate-y-1"
                                  enterTo="opacity-100 scale-100 translate-y-0"
                                  leave="transition ease-in duration-75"
                                  leaveFrom="opacity-100 scale-100 translate-y-0"
                                  leaveTo="opacity-0 scale-95 translate-y-1"
                                >
                                  <PopoverPanel
                                    className="absolute left-0 top-full z-50 mt-1 max-w-[200px] rounded-lg border border-gray-200 bg-white p-2 shadow-lg dark:border-dark-600 dark:bg-dark-800 outline-none focus:outline-none flex flex-col gap-1.5"
                                  >
                                    {otherTags.map((tag, idx) => {
                                      const otherTagText = typeof tag === "string" ? tag : tag?.TagDisplayName || tag?.name || "";
                                      return (
                                        <span
                                          key={tag.TagId || idx}
                                          title={otherTagText}
                                          className="inline-flex items-center truncate rounded-full bg-green-100 px-2.5 py-0.5 text-[10px] font-medium text-green-800 dark:bg-green-900/30 dark:text-green-300 w-fit"
                                        >
                                          {otherTagText}
                                        </span>
                                      );
                                    })}
                                  </PopoverPanel>
                                </Transition>
                              </>
                            )}
                          </Popover>
                        );
                      })()
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>


        <div className={`mt-2 flex justify-center lg:mt-0 lg:text-right`}>
          <div className="flex flex-col items-center">
            <div
              className={`dark:text-dark-50 mb-1 text-2xl font-bold text-gray-900 lg:text-3xl`}
            >
              <Circlebar
                value={
                  leadData.leadScore === "Unscored"
                    ? 0
                    : leadData.leadScore || 0
                }
                size={20}
                strokeWidth={5}
                color={getLeadScoreColor(leadData.leadScore)}
                className="mx-auto"
              >
                <div className="text-center">
                  <div
                    className={`dark:text-dark-50 text-lg font-bold text-gray-900 lg:text-xl`}
                  >
                    {leadData.leadScore === "Unscored"
                      ? "--"
                      : leadData.leadScore || 0}
                  </div>
                  <div className={`dark:text-dark-300 text-xs text-gray-500`}>
                    Score
                  </div>
                </div>
              </Circlebar>
            </div>
            <div className={`dark:text-dark-300 text-xs text-gray-500 sm:text-sm`}>
              Lead Score
            </div>
            <div className="mt-3">
              <Button

                variant="soft"
                color="primary"
                size="sm"
                className="inline-flex items-center gap-2 ms-2 mb-2 sm:mb-0"
                onClick={() => setShowTimelineDrawer(true)}
              >
                View {resolvedEntityName} Timeline
              </Button>
            </div>
            <div className={`mt-2 space-y-1 text-center sm:mt-4 sm:space-y-2`}>
              <div className="flex flex-wrap justify-center gap-1">
                {/* {leadData.tags.map((tag, index) => (
                <Badge key={index} variant="outlined" color="info" className="text-xs">
                  {tag}
                </Badge>
              ))} */}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showTimelineDrawer && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40"
          onClick={() => setShowTimelineDrawer(false)}>
          <div className="w-full max-w-lg bg-white dark:bg-dark-700 h-full shadow-xl"
          onClick={(e) => e.stopPropagation()}>

            {/* Header */}
            <div className="flex items-center justify-between border-b p-4">
              <h2 className="text-lg font-semibold">{resolvedEntityName} Timeline</h2>
              <Button
                variant="flat"
                isIcon
                onClick={() => setShowTimelineDrawer(false)}
              >
                <XMarkIcon className="size-5" />
              </Button>
            </div>

            {/* Timeline */}
            <div className="h-[calc(100%-60px)] overflow-hidden p-4">
              <LeadTimeline
                leadId={leadId}
                isActive={showTimelineDrawer}
                entityName={resolvedEntityName}
              />
            </div>

          </div>
        </div>
      )}

      {leadData?.HasDuplicate && (
        <ReactTooltip
          id={duplicateTooltipId}
          anchorId={duplicateAnchorId}
          clickable
          openOnClick={true}
          openOnHover={false}
          place="bottom"
          offset={12}
          className="!z-[120] !rounded-xl !border !p-0 !shadow-xl [--rt-bg-color:#ffffff] [--rt-border-color:rgba(251,191,36,0.65)] dark:[--rt-bg-color:#0f172a] dark:[--rt-border-color:rgba(251,191,36,0.35)]"
          isOpen={showDuplicates}
          setIsOpen={(open) => {
            if (open && !showDuplicates) {
              setShowDuplicates(true);
            }
          }}
          closeOnEsc={false}
          closeOnScroll={false}
          keepTooltipInside
          positionStrategy="fixed"
          arrowColor="#f97316"
          style={{
            boxShadow: "0 18px 45px rgba(15, 23, 42, 0.15)",
            opacity: 1,
            backdropFilter: "none",
            WebkitBackdropFilter: "none",
            "--rt-opacity": "1",
          }}
        >
          <div className="relative w-[min(340px,92vw)] sm:w-[min(520px,80vw)] md:w-[min(560px,72vw)] lg:w-[min(640px,65vw)] xl:w-[min(880px,55vw)] max-h-[70vh] lg:max-h-[80vh] overflow-y-auto rounded-xl bg-white dark:bg-slate-900">
            <div className="sticky top-0 z-20 mb-4 flex items-center justify-between gap-4 border-b border-amber-100/70 bg-white px-4 pb-3 pt-4 shadow-sm dark:border-amber-500/20 dark:bg-slate-900 sm:px-5">
              <div className="flex items-center gap-3">
                <div>
                  <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                    Duplicate Leads
                  </h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Linked with #{leadData?.displayId || leadData?.id}
                  </p>
                </div>
                <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-700 dark:bg-amber-500/30 dark:text-amber-100">
                  {duplicates.length}
                </span>
              </div>
              <button
                onClick={() => setShowDuplicates(false)}
                className="rounded-full p-1 text-gray-500 transition hover:bg-gray-100 hover:text-gray-700 dark:text-gray-300 dark:hover:bg-slate-800"
              >
                <XMarkIcon className="size-5" />
              </button>
            </div>

            {isLoadingDuplicates && duplicates.length === 0 ? (
              <div className="flex items-center justify-center gap-3 py-6 text-amber-700 dark:text-amber-200">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-amber-200 border-t-amber-500" />
                <span className="text-sm font-medium">Loading duplicates…</span>
              </div>
            ) : duplicates.length === 0 ? (
              <p className="py-4 text-center text-sm text-gray-600 dark:text-dark-300">
                No duplicate leads found.
              </p>
            ) : (
              <div className=" p-4 pt-0 grid gap-4 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3">
                {duplicates.map((duplicate) => (
                  <DuplicateLeadCard
                    key={
                      duplicate?.LeadDuplicateId ||
                      `${duplicate?.DuplicateLeadId}-${duplicate?.MatchType}`
                    }
                    duplicate={duplicate}
                    isManager={isManager}
                    viewOnly
                  />
                ))}
              </div>
            )}
          </div>
        </ReactTooltip>
      )}

      <EmailMessageModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        leadData={leadData}
      />
    </>
  );
};

export default LeadBasicInfo;
